/**
 * Report link-sharing ACL stored on the view under `access`.
 * Shared by the Jepto owner UI and the analytics viewer gate.
 */
export type ViewAccessMode = 'PUBLIC' | 'PRIVATE';

export type ViewAccessStatus = 'ACTIVE' | 'DISABLED';

/** Result of `Mutation.sendViewShareCode` for guest OTP on PRIVATE shares. */
export type ViewShareCodeStatus = 'CODE_SENT' | 'ACCESS_REQUIRED';

/** AppSync `AccessItem` — share config read back from `getItem.view`. */
export interface ViewAccessConfig {
	accessMode?: ViewAccessMode | null;
	status?: ViewAccessStatus | null;
	/** Epoch seconds; `null` means no hard expiry. */
	expiresAt?: number | null;
	/** Lowercased emails allowed on a PRIVATE share. */
	accessList?: string[] | null;
	/** Legacy role/user ACL kept so patches never drop existing values. */
	roles?: string[] | null;
	users?: string[] | null;
	public?: boolean | null;
}

/** Guest-safe client branding on the share gate (subset of `ClientItem`). */
export interface ViewAccessClient {
	name?: string | null;
	logo?: string | null;
}

/** Guest-safe gate metadata from `getViewAccess` (never carries the accessList). */
export interface ViewAccessPublic {
	reportId: string;
	viewId: string;
	accountId: string;
	accessMode: ViewAccessMode;
	status: ViewAccessStatus;
	expiresAt?: number | null;
	client?: ViewAccessClient | null;
}

/** `getViewAccess.client` fields for the share gate header. */
export function clientBrandingFromViewAccess(
	access: ViewAccessPublic
): { name?: string; logo?: string } {
	const name = access.client?.name?.trim();
	const logo = access.client?.logo?.trim();
	return {
		name: name || undefined,
		logo: logo || undefined,
	};
}

/** Written the first time an owner enables sharing on a report. */
export const DEFAULT_VIEW_ACCESS: ViewAccessConfig = {
	accessMode: 'PUBLIC',
	status: 'ACTIVE',
	accessList: [],
	expiresAt: null,
};

export function isViewAccessExpired(
	access: Pick<ViewAccessConfig, 'expiresAt'> | null | undefined,
	nowEpochSeconds: number = Math.floor(Date.now() / 1000)
): boolean {
	const expiresAt = access?.expiresAt;
	return typeof expiresAt === 'number' && expiresAt > 0
		? expiresAt <= nowEpochSeconds
		: false;
}

/** True when the share is enabled and still within its expiry window. */
export function isViewAccessActive(
	access: ViewAccessConfig | ViewAccessPublic | null | undefined
): boolean {
	if (!access || access.status !== 'ACTIVE') {
		return false;
	}
	return !isViewAccessExpired(access);
}

export function normalizeAccessEmail(email: string): string {
	return email.trim().toLowerCase();
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidAccessEmail(email: string): boolean {
	return EMAIL_PATTERN.test(normalizeAccessEmail(email));
}

type LegacyAccessApi = Partial<ViewAccessConfig> | null | undefined;

/**
 * Map AppSync `AccessItem` to share UI shape. Works with legacy fields (`public` only)
 * until the JEP-10226 schema (`accessMode`, `status`, …) is deployed.
 */
export function normalizeViewAccessFromApi(raw: LegacyAccessApi): ViewAccessConfig | null {
	if (!raw) {
		return null;
	}

	const explicitMode = String(raw.accessMode ?? '')
		.trim()
		.toUpperCase();
	const explicitStatus = String(raw.status ?? '')
		.trim()
		.toUpperCase();

	if (explicitMode === 'PUBLIC' || explicitMode === 'PRIVATE') {
		return {
			accessMode: explicitMode as ViewAccessMode,
			status:
				explicitStatus === 'DISABLED'
					? 'DISABLED'
					: ('ACTIVE' as ViewAccessStatus),
			expiresAt: raw.expiresAt ?? null,
			accessList: raw.accessList ?? [],
			roles: raw.roles ?? null,
			users: raw.users ?? null,
			public: raw.public ?? null,
		};
	}

	const status: ViewAccessStatus =
		explicitStatus === 'DISABLED' || explicitStatus === 'ACTIVE'
			? (explicitStatus as ViewAccessStatus)
			: raw.public === false
				? 'DISABLED'
				: 'ACTIVE';

	return {
		accessMode: 'PUBLIC',
		status,
		expiresAt: raw.expiresAt ?? null,
		accessList: raw.accessList ?? [],
		roles: raw.roles ?? null,
		users: raw.users ?? null,
		public: raw.public ?? null,
	};
}
